/**
 * The one place the browser talks to the bench.
 *
 * Nothing here has, or can obtain, an access token: the session is an HttpOnly cookie the page
 * cannot read, and every AMS call is made server-side.
 */

/** A failed bench call, carrying enough context to show the tester something actionable. */
export class BenchApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "BenchApiError";
    this.status = status;
  }

  /** No session yet, or it expired — the caller should show the connect panel. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal } = options;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: {
        Accept: "application/json",
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    // Network-level failure: the bench process itself is unreachable. Status 0 distinguishes
    // this from an HTTP error, and the original error is kept as `cause` for the console.
    throw new BenchApiError(
      `Could not reach the test bench at ${path}. Is the bench process running?`,
      0,
      { cause },
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    // FastAPI puts the human-readable reason in `detail`; the bench writes those specifically
    // for testers, so prefer it over any status-code phrasing of our own.
    const detail =
      payload && typeof payload === "object" && "detail" in payload
        ? (payload as { detail: unknown }).detail
        : null;
    throw new BenchApiError(
      typeof detail === "string" && detail
        ? detail
        : `${path} failed with HTTP ${response.status}`,
      response.status,
    );
  }

  return payload as T;
}
